import { TipContent, TipSection } from "@/constants/tip";
import { Button, colors, customEvent, Link, NextLink } from "goi_common";
import { Category } from "@/types/guidebookType";
import Table from "@/components/Guidebook/Table";
import { Fragment, useMemo } from "react";
import NotionBlock from "./NotionBlock";
import { CdnImage } from "./CdnImage";

import parse, { HTMLReactParserOptions, domToReact, Element } from "html-react-parser";
import { css } from "@emotion/react";
import { Stack } from "@chakra-ui/react";

interface GuidebookTipContentProps {
  contents?: TipContent[];
  category: Category;
  index: number;
  html?: string;
}

function GuidebookTipContent({ contents, category, index, html }: GuidebookTipContentProps) {
  const parseHTML = useMemo(() => {
    if (!html) return;

    let buttonIndexInContent = 0;

    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode instanceof Element && domNode?.attribs) {
          const { attribs, children } = domNode;

          if (attribs["data-trigger"] && attribs?.href) {
            const currentIndex = buttonIndexInContent + 1;
            buttonIndexInContent = buttonIndexInContent + 1;

            const slug = `tip-${category}-${index}`;

            const triggerAndTarget: `${string}-CTA_${number}` = `${slug}-CTA_${currentIndex}`;

            return (
              <Link
                key={`buttonIndexInContent-${currentIndex}`}
                href={attribs.href}
                css={css(attribs?.style)}
                data-trigger={triggerAndTarget}
                className={attribs?.class}
                onClick={() => {
                  customEvent({
                    newGtm: {
                      path: "gdb_tip",
                      trigger_and_target: triggerAndTarget,
                    },
                  });
                }}
              >
                {domToReact(children as any, options)}
              </Link>
            );
          }
        }
        return domNode;
      },
    };

    return parse(html.replaceAll("\n", ""), options);
  }, [html]);

  return (
    <>
      {contents &&
        contents.map((content) => (
          <section key={JSON.stringify(content)} style={{ marginTop: "48px" }}>
            {Object.entries(content).map(([key, value]) => {
              if (key === "head") {
                return (
                  <h2 key={value} className="subtitle2_b">
                    {value}
                  </h2>
                );
              } else if (key === "img") {
                let src = `/images/guidebook/tip/embed/${category}/${index}.png`;
                let alt = value;

                if (typeof value !== "string") {
                  src = `/images/guidebook/tip/embed/${category}/${index}-${value.index}.png`;
                  alt = value.alt;
                }

                return <CdnImage key={alt} src={src} alt={alt} />;
              } else if (key === "section") {
                return (
                  <Fragment key={JSON.stringify(value)}>
                    {value.map((section: TipSection) => (
                      <section
                        key={JSON.stringify(section)}
                        className="body3_r"
                        style={{ marginTop: "8px" }}
                      >
                        {Object.entries(section).map(([key, value]) => {
                          if (key === "strong") {
                            return (
                              <strong
                                className="body3_m"
                                style={{ fontWeight: "bold" }}
                                key={value}
                                dangerouslySetInnerHTML={{ __html: value }}
                              />
                            );
                          } else if (key === "div") {
                            return (
                              <p
                                className="body3_r"
                                style={{ marginTop: "8px" }}
                                key={value}
                                dangerouslySetInnerHTML={{ __html: value }}
                              />
                            );
                          } else if (key === "table") {
                            return (
                              <Table
                                key={JSON.stringify(value.head)}
                                head={value.head}
                                body={value.body}
                              />
                            );
                          } else if (key === "link") {
                            return (
                              <NextLink key={value.text} href={value.href}>
                                {value.text}
                              </NextLink>
                            );
                          } else if (key === "button") {
                            return (
                              <Stack key={`button-${value.text}`} alignItems="center">
                                <NextLink href={value.href}>
                                  <Button
                                    css={{
                                      background: colors.gray900,
                                      color: colors.white,
                                      padding: "8px 12px",
                                      borderRadius: "8px",
                                      width: "210px",
                                    }}
                                  >
                                    {value.text}
                                  </Button>
                                </NextLink>
                              </Stack>
                            );
                          }
                        })}
                      </section>
                    ))}
                  </Fragment>
                );
              }
            })}
          </section>
        ))}
      {parseHTML && <NotionBlock className="body3_r">{parseHTML}</NotionBlock>}
    </>
  );
}

export default GuidebookTipContent;
